import { useRef, useState, useMemo } from 'react';
import { Formik, FormikProps } from 'formik';
import { useRouter } from '@uirouter/react';

import { notifySuccess } from '@/portainer/services/notifications';
import { useAnalytics } from '@/react/hooks/useAnalytics';
import { useCanExit } from '@/react/hooks/useCanExit';
import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';
import { useNamespacesQuery } from '@/react/kubernetes/namespaces/queries/useNamespacesQuery';
import {
  KubernetesStackPayload,
  useCreateKubernetesStackMutation,
} from '@/react/kubernetes/stacks/queries/useCreateStackMutation';
import { createWebhookId } from '@/portainer/helpers/webhookHelper';
import { transformAutoUpdateViewModel } from '@/react/portainer/gitops/AutoUpdateFieldset/utils';

import { confirm, confirmGenericDiscard } from '@@/modals/confirm';

import { useUpdateHelmReleaseMutation } from '../helmReleaseQueries/useUpdateHelmReleaseMutation';

import { HelmInstallFormValues } from './types';
import { helmInstallFormValidationSchema } from './validation';
import { HelmInstallInnerForm } from './HelmInstallInnerForm';

export function HelmInstallForm() {
  const environmentId = useEnvironmentId();
  const router = useRouter();
  const analytics = useAnalytics();

  const [previewIsValid, setPreviewIsValid] = useState(false);

  const namespacesQuery = useNamespacesQuery(environmentId);
  const namespaces = Object.values(namespacesQuery.data ?? {}).map((ns) => ({
    label: ns.Name,
    value: ns.Name,
  }));

  const defaultNamespace =
    namespaces.find((ns) => ns.value === 'default')?.value ||
    namespaces[0]?.value ||
    '';

  const formikRef = useRef<FormikProps<HelmInstallFormValues>>(null);

  const initialValues: HelmInstallFormValues = useMemo(
    () => ({
      useGitOps: true,
      // Git configuration
      RepositoryURL: '',
      RepositoryURLValid: false,
      // Helm-specific Git fields
      HelmChartPath: '',
      HelmValuesFiles: [],
      RepositoryReferenceName: '',
      TLSSkipVerify: false,
      RepositoryAuthentication: false,
      RepositoryUsername: '',
      RepositoryPassword: '',
      // Auto update configuration
      AutoUpdate: {
        RepositoryAutomaticUpdates: false,
        RepositoryMechanism: 'Interval',
        RepositoryFetchInterval: '5m',
        ForcePullImage: false,
        RepositoryAutomaticUpdatesForce: false,
      },
      webhookId: createWebhookId(),
      // Atomic option (rollback on failure)
      atomic: false,
      // Deploy configuration
      namespace: defaultNamespace,
      name: '',
      version: '',
      values: '',
      repo: '',
      registryId: undefined,
      chartName: undefined,
      chartRepo: undefined,
    }),
    [defaultNamespace]
  );

  useCanExit(() => !formikRef.current?.dirty || confirmGenericDiscard());

  const installHelmChartMutation = useUpdateHelmReleaseMutation(environmentId);
  const createStackMutation = useCreateKubernetesStackMutation(environmentId);

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      validationSchema={helmInstallFormValidationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
      validateOnChange
      validateOnBlur
      validateOnMount
    >
      {() => (
        <HelmInstallInnerForm
          namespaces={namespaces}
          setPreviewIsValid={setPreviewIsValid}
        />
      )}
    </Formik>
  );

  async function handleSubmit(values: HelmInstallFormValues) {
    if (values.useGitOps) {
      await gitHelmInstall(values);
    } else {
      await manualHelmInstall(values);
    }
  }

  async function gitHelmInstall(values: HelmInstallFormValues) {
    if (!previewIsValid) {
      const confirmed = await confirm({
        title: 'Chart validation failed',
        message:
          'The Helm manifest preview validation failed, which may indicate configuration issues. This can be normal when creating new resources. Do you want to proceed with the installation?',
      });
      if (!confirmed) {
        return;
      }
    }

    const payload: KubernetesStackPayload = {
      method: 'repository', // Git deployment method
      type: 'kubernetes', // Kubernetes stack type
      StackName: values.name.trim(),
      ComposeFormat: false, // Use manifest format for Helm
      Namespace: values.namespace,
      RepositoryURL: values.RepositoryURL,
      RepositoryReferenceName: values.RepositoryReferenceName || '',
      RepositoryAuthentication: values.RepositoryAuthentication || false,
      RepositoryUsername: values.RepositoryUsername || '',
      RepositoryPassword: values.RepositoryPassword || '',
      RepositoryAuthorizationType: values.RepositoryAuthorizationType || 0,
      RepositoryGitCredentialID: values.RepositoryGitCredentialID || 0,
      ManifestFile: '', // Not used for Helm

      // This is a workaround to use the root directory of the repo as the chart path.
      // The backend uses the value of the Helm Chart Path to determine if the Kubernetes Stack deployment is a Helm Chart or a Manifest deployment.
      // If it has a value then it's assumed to be a Helm Chart deployment, otherwise it's assumed to be a Manifest deployment. Since changing this
      // in the backend would be a breaking change I need to pass a non-empty path here.
      HelmChartPath: values.HelmChartPath || './', // Helm chart folder path

      AdditionalFiles: [], // Used for k8s deployments, empty for Helm
      HelmValuesFiles: values.HelmValuesFiles || [], // Helm values files
      TLSSkipVerify: values.TLSSkipVerify || false,
      // Auto update configuration
      AutoUpdate: transformAutoUpdateViewModel(
        values.AutoUpdate,
        values.webhookId
      ),
    };

    await createStackMutation.mutateAsync(payload, {
      onSuccess() {
        analytics.trackEvent('kubernetes-helm-git-install', {
          category: 'kubernetes',
        });
        notifySuccess(
          'Success',
          'Helm chart from Git repository successfully deployed'
        );
        formikRef.current?.resetForm();
        router.stateService.go('kubernetes.applications');
      },
    });
  }

  async function manualHelmInstall(values: HelmInstallFormValues) {
    if (!previewIsValid) {
      const confirmed = await confirm({
        title: 'Chart validation failed',
        message:
          'The Helm manifest preview validation failed, which may indicate configuration issues. This can be normal when creating new resources. Do you want to proceed with the installation?',
      });
      if (!confirmed) {
        return;
      }
    }

    if (!values.chartName) {
      return;
    }

    await installHelmChartMutation.mutateAsync(
      {
        name: values.name.trim(),
        namespace: values.namespace,
        chart: values.chartName,
        version: values.version,
        values: values.values,
        repo: values.registryId ? undefined : values.chartRepo,
        registryId: values.registryId,
      },
      {
        onSuccess() {
          analytics.trackEvent('kubernetes-helm-install', {
            category: 'kubernetes',
          });
          notifySuccess('Success', 'Helm chart successfully installed');

          formikRef.current?.resetForm();
          router.stateService.go('kubernetes.applications');
        },
      }
    );
  }
}
